/***
*
*   WORKSPACE DETAIL
*   View detailed information about a single workspace
*
**********/

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Tabs, Descriptions, Table, Tag, Button, Space, Typography,
  Row, Col, Statistic, Progress, Spin, Empty, Breadcrumb, message, Modal,
  Badge
} from 'antd';
import {
  InfoCircleOutlined, TeamOutlined, ProjectOutlined,
  CreditCardOutlined, SettingOutlined, DownloadOutlined,
  PoweroffOutlined, CheckCircleOutlined, CloseCircleOutlined,
  HomeOutlined, CloudServerOutlined, CalendarOutlined,
  ArrowLeftOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined
} from '@ant-design/icons';
import {
  fetchWorkspaceDetail,
  fetchWorkspaceUsers,
  fetchWorkspaceEmployees,
  fetchWorkspaceBilling,
  fetchWorkspaceFeatures,
  invalidateWorkspaceDetailCache
} from '../store/slices/workspaceDetailSlice';
import { deactivateWorkspace, activateWorkspace } from '../store/slices/workspacesSlice';
import { Animate, usePermissions } from 'components/lib';
import Axios from 'axios';

const { Title, Text } = Typography;
const { confirm } = Modal;

export function WorkspaceDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: workspaceId } = useParams();
  const { can } = usePermissions();

  const [activeTab, setActiveTab] = useState('overview');
  const [usersPage, setUsersPage] = useState(1);
  const [employeesPage, setEmployeesPage] = useState(1);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [showCardNumber, setShowCardNumber] = useState(false);

  const detailState = useSelector((state) => state.workspaceDetail);

  const ws = detailState.details[workspaceId];
  const usersCacheKey = `${workspaceId}_users_${usersPage}_25`;
  const employeesCacheKey = `${workspaceId}_employees_${employeesPage}_25`;
  const billingCacheKey = `${workspaceId}_billing`;
  const featuresCacheKey = `${workspaceId}_features`;

  const usersData = detailState.users[usersCacheKey];
  const employeesData = detailState.employees[employeesCacheKey];
  const billingData = detailState.billing[billingCacheKey];
  const featuresData = detailState.features[featuresCacheKey];

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchWorkspaceDetail(workspaceId));
      dispatch(fetchWorkspaceUsers({ workspaceId, page: usersPage, limit: 25 }));
      dispatch(fetchWorkspaceEmployees({ workspaceId, page: employeesPage, limit: 25 }));
      dispatch(fetchWorkspaceBilling(workspaceId));
      dispatch(fetchWorkspaceFeatures(workspaceId));
    }
  }, [dispatch, workspaceId, usersPage, employeesPage]);

  const handleRefresh = () => {
    dispatch(invalidateWorkspaceDetailCache(workspaceId));
    dispatch(fetchWorkspaceDetail(workspaceId));
    dispatch(fetchWorkspaceUsers({ workspaceId, page: usersPage, limit: 25 }));
    dispatch(fetchWorkspaceEmployees({ workspaceId, page: employeesPage, limit: 25 }));
    dispatch(fetchWorkspaceBilling(workspaceId));
    dispatch(fetchWorkspaceFeatures(workspaceId));
  };

  if (detailState.loading.detail && !ws) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="Loading workspace..." />
      </div>
    );
  }

  if (!ws) {
    return (
      <Card>
        <Empty description="Workspace not found">
          <Button type="primary" onClick={() => navigate('/workspaces')}>
            Back to Workspaces
          </Button>
        </Empty>
      </Card>
    );
  }

  const handleToggleStatus = () => {
    const action = ws.active ? 'deactivate' : 'activate';
    confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Workspace`,
      content: ws.active
        ? `Are you sure you want to deactivate "${ws.name}"? Users will not be able to log in.`
        : `Are you sure you want to activate "${ws.name}"?`,
      okText: action.charAt(0).toUpperCase() + action.slice(1),
      okButtonProps: { danger: ws.active },
      onOk: async () => {
        setMutationLoading(true);
        try {
          if (ws.active) {
            await dispatch(deactivateWorkspace(workspaceId)).unwrap();
          } else {
            await dispatch(activateWorkspace(workspaceId)).unwrap();
          }
          message.success(`Workspace ${action}d successfully`);
          dispatch(invalidateWorkspaceDetailCache(workspaceId));
          dispatch(fetchWorkspaceDetail(workspaceId));
        } catch {
          message.error(`Failed to ${action} workspace`);
        }
        setMutationLoading(false);
      },
    });
  };

  const handleExport = async () => {
    try {
      const response = await Axios.get(`/api/dashboard/workspaces/${workspaceId}/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `workspace-${ws.slug || workspaceId}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Export started');
    } catch {
      message.error('Failed to export data');
    }
  };

  const usersColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (text) => <Text strong>{text}</Text> },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Role', dataIndex: 'role', key: 'role', render: (text) => <Tag color="purple">{text}</Tag> },
    { title: 'Account Type', dataIndex: 'account_type', key: 'account_type', render: (text) => <Tag color="blue">{text}</Tag> },
    {
      title: 'Verified',
      dataIndex: 'verified',
      key: 'verified',
      render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
    },
    {
      title: 'Last Active',
      dataIndex: 'last_active',
      key: 'last_active',
      render: (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : <Text type="secondary">Never</Text>
    },
  ];

  const employeesColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (text) => <Text strong>{text}</Text> },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Department', dataIndex: 'department', key: 'department', render: (text) => text !== 'N/A' ? <Tag>{text}</Tag> : <Text type="secondary">{text}</Text> },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (text) => <Tag color={text === 'Active' ? 'green' : 'default'}>{text}</Tag> },
    { title: 'Hire Date', dataIndex: 'hire_date', key: 'hire_date' },
  ];

  const usersTableData = usersData?.results?.map(u => ({
    key: u.id || u._id,
    name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'N/A',
    email: u.email,
    role: u.workspaces?.length ? u.workspaces[0].role_name : 'N/A',
    account_type: u.workspaces?.length ? u.workspaces[0].account_type?.toUpperCase() : 'N/A',
    verified: u.verified,
    last_active: u.last_active,
  })) || [];

  const employeesTableData = employeesData?.results?.map(e => ({
    key: e.id || e._id,
    name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'N/A',
    email: e.email || 'N/A',
    department: e.department_option || 'N/A',
    position: e.job_title_option || 'N/A',
    status: e.status_option || 'Active',
    hire_date: e.joining_date ? new Date(e.joining_date).toLocaleDateString() : 'N/A',
  })) || [];

  const storageUsed = ws.stats?.feature_usage?.usage?.file_storage || 0;
  const storageLimit = ws.features?.file_storage?.limit || 0;
  const storagePercent = storageLimit > 0 ? Math.min((storageUsed / storageLimit) * 100, 100).toFixed(1) : 0;

  const tabItems = [
    {
      key: 'overview',
      label: <span><InfoCircleOutlined /> Overview</span>,
      children: (
        <div>
          {/* Stats Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card
                hoverable
                onClick={() => setActiveTab('users')}
                style={{ cursor: 'pointer' }}
              >
                <Statistic
                  title="Users"
                  value={ws.stats?.user_count || 0}
                  prefix={<TeamOutlined style={{ color: '#7c3aed' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card
                hoverable
                onClick={() => setActiveTab('employees')}
                style={{ cursor: 'pointer' }}
              >
                <Statistic
                  title="Employees"
                  value={ws.stats?.employee_count || 0}
                  prefix={<ProjectOutlined style={{ color: '#2563eb' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card hoverable>
                <Statistic
                  title="Storage Used"
                  value={`${storageUsed.toFixed(2)} MB`}
                  prefix={<CloudServerOutlined style={{ color: '#059669' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card hoverable>
                <Statistic
                  title="Created"
                  value={new Date(ws.date_created || ws.createdAt).toLocaleDateString()}
                  prefix={<CalendarOutlined style={{ color: '#d97706' }} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Details Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card title={<><InfoCircleOutlined /> Workspace Information</>} size="small">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="ID">
                    <Text copyable={{ text: ws.id || ws._id }}>{(ws.id || ws._id)?.slice(0, 12)}...</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Name">{ws.name || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Slug">
                    <Text code>{ws.slug || 'N/A'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Owner">{ws.owner_name || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Owner Email">
                    <Text copyable>{ws.owner_email || 'N/A'}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title={<><CreditCardOutlined /> Plan Details</>} size="small">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Plan">
                    <Tag color="blue">{ws.plan_name || ws.plan || 'Free'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={ws.active ? 'green' : 'red'} icon={ws.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {ws.active ? 'Active' : 'Inactive'}
                    </Tag>
                  </Descriptions.Item>
                  {ws.trial_end_date && (
                    <Descriptions.Item label="Trial Ends">
                      <Text type={new Date(ws.trial_end_date) < new Date() ? 'danger' : 'warning'}>
                        {new Date(ws.trial_end_date).toLocaleDateString()}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Billing Interval">
                    <Tag>{ws.billing_interval || 'monthly'}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title={<><CloudServerOutlined /> Storage</>} size="small">
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Progress
                    type="dashboard"
                    percent={Number(storagePercent)}
                    status={storagePercent > 90 ? 'exception' : storagePercent > 70 ? 'active' : 'success'}
                    format={() => (
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 'bold' }}>{storageUsed.toFixed(1)}</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>MB</div>
                      </div>
                    )}
                    size={120}
                  />
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Used">{storageUsed.toFixed(2)} MB</Descriptions.Item>
                  <Descriptions.Item label="Allocated">{storageLimit > 0 ? `${storageLimit} MB` : 'Unlimited'}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined /> Users
          <Badge count={usersData?.total || 0} style={{ marginLeft: 8, backgroundColor: '#7c3aed' }} showZero />
        </span>
      ),
      children: (
        <Card>
          <Table
            columns={usersColumns}
            dataSource={usersTableData}
            loading={detailState.loading.users}
            pagination={{
              current: usersPage,
              pageSize: 25,
              total: usersData?.total || 0,
              onChange: (page) => setUsersPage(page),
              showTotal: (total) => `Total ${total} users`,
              showSizeChanger: false,
            }}
            locale={{ emptyText: <Empty description="No users found" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'employees',
      label: (
        <span>
          <ProjectOutlined /> Employees
          <Badge count={employeesData?.total || 0} style={{ marginLeft: 8, backgroundColor: '#7c3aed' }} showZero />
        </span>
      ),
      children: (
        <Card>
          <Table
            columns={employeesColumns}
            dataSource={employeesTableData}
            loading={detailState.loading.employees}
            pagination={{
              current: employeesPage,
              pageSize: 25,
              total: employeesData?.total || 0,
              onChange: (page) => setEmployeesPage(page),
              showTotal: (total) => `Total ${total} employees`,
              showSizeChanger: false,
            }}
            locale={{ emptyText: <Empty description="No employees found" /> }}
          />
        </Card>
      ),
    },
    {
      key: 'billing',
      label: <span><CreditCardOutlined /> Billing</span>,
      children: (
        <Spin spinning={detailState.loading.billing}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Subscription" size="small">
                {billingData?.subscription ? (
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Plan">{ws.plan_name || ws.plan}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={billingData.subscription.status === 'active' ? 'green' : 'orange'}>
                        {billingData.subscription.status?.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Current Period End">
                      {billingData.subscription.current_period_end
                        ? new Date(billingData.subscription.current_period_end * 1000).toLocaleDateString()
                        : 'N/A'}
                    </Descriptions.Item>
                    {billingData.subscription.cancel_at_period_end && (
                      <Descriptions.Item label="Cancellation">
                        <Tag color="red">Cancels at period end</Tag>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                ) : (
                  <Empty description="No active subscription" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Payment Method" size="small">
                {billingData?.payment_method?.data?.length > 0 ? (
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Type">
                      <Tag>{billingData.payment_method.data[0].type?.toUpperCase()}</Tag>
                    </Descriptions.Item>
                    {billingData.payment_method.data[0].card && (
                      <>
                        <Descriptions.Item label="Brand">
                          <Tag color="blue">{billingData.payment_method.data[0].card.brand?.toUpperCase()}</Tag>
                          <Tag>{billingData.payment_method.data[0].card.funding?.toUpperCase()}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Card">
                          <Space>
                            {showCardNumber ? (
                              <Text>**** **** **** {billingData.payment_method.data[0].card.last4}</Text>
                            ) : (
                              <Text>**** **** **** {billingData.payment_method.data[0].card.last4}</Text>
                            )}
                            <Button
                              type="text"
                              size="small"
                              icon={showCardNumber ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                              onClick={() => setShowCardNumber(!showCardNumber)}
                            />
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Expires">
                          {billingData.payment_method.data[0].card.exp_month}/{billingData.payment_method.data[0].card.exp_year}
                        </Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                ) : (
                  <Empty description="No payment method on file" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>
          </Row>
          <Card title="Recent Invoices" style={{ marginTop: 16 }}>
            {billingData?.invoices?.data?.length > 0 ? (
              <Table
                dataSource={billingData.invoices.data.map(inv => ({
                  key: inv.id,
                  date: new Date(inv.created * 1000).toLocaleDateString(),
                  amount: `$${(inv.amount_paid / 100).toFixed(2)}`,
                  status: inv.status,
                  pdf: inv.invoice_pdf,
                }))}
                columns={[
                  { title: 'Date', dataIndex: 'date', key: 'date' },
                  { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (amt) => <Text strong>{amt}</Text> },
                  { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'paid' ? 'green' : 'orange'}>{s?.toUpperCase()}</Tag> },
                  {
                    title: 'Action',
                    key: 'action',
                    render: (_, record) => record.pdf && (
                      <Button type="link" icon={<DownloadOutlined />} onClick={() => window.open(record.pdf, '_blank')}>
                        Download
                      </Button>
                    ),
                  },
                ]}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="No invoices found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
          <Card title="Next Billing" style={{ marginTop: 16 }}>
            {billingData?.nextBilling ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Amount Due">
                      <Text strong>${(billingData.nextBilling.amount_due / 100).toFixed(2)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={billingData.nextBilling.status === 'draft' ? 'blue' : 'orange'}>
                        {billingData.nextBilling.status?.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Next Payment Attempt">
                      {billingData.nextBilling.next_payment_attempt
                        ? new Date(billingData.nextBilling.next_payment_attempt * 1000).toLocaleString()
                        : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Billing Period">
                      {billingData.nextBilling.period_start && billingData.nextBilling.period_end
                        ? `${new Date(billingData.nextBilling.period_start * 1000).toLocaleDateString()} - ${new Date(billingData.nextBilling.period_end * 1000).toLocaleDateString()}`
                        : 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} md={12}>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Subtotal">
                      ${(billingData.nextBilling.subtotal / 100).toFixed(2)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tax">
                      ${billingData.nextBilling.tax ? (billingData.nextBilling.tax / 100).toFixed(2) : '0.00'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total">
                      <Text strong>${(billingData.nextBilling.total / 100).toFixed(2)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Collection Method">
                      <Tag>{billingData.nextBilling.collection_method?.replace(/_/g, ' ')?.toUpperCase()}</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                {billingData.nextBilling.lines?.data?.length > 0 && (
                  <Col xs={24}>
                    <Table
                      dataSource={billingData.nextBilling.lines.data.map((line, idx) => ({
                        key: line.id || idx,
                        description: line.description,
                        quantity: line.quantity,
                        amount: `$${(line.amount / 100).toFixed(2)}`,
                        period: `${new Date(line.period.start * 1000).toLocaleDateString()} - ${new Date(line.period.end * 1000).toLocaleDateString()}`,
                      }))}
                      columns={[
                        { title: 'Description', dataIndex: 'description', key: 'description' },
                        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                        { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (amt) => <Text strong>{amt}</Text> },
                        { title: 'Period', dataIndex: 'period', key: 'period' },
                      ]}
                      pagination={false}
                      size="small"
                      title={() => <Text strong>Line Items</Text>}
                    />
                  </Col>
                )}
              </Row>
            ) : (
              <Empty description="No upcoming billing information" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Spin>
      ),
    },
    {
      key: 'features',
      label: <span><SettingOutlined /> Features</span>,
      children: (
        <Spin spinning={detailState.loading.features}>
          <Card title="Feature Usage & Limits">
            {featuresData?.limitKeyData?.length > 0 ? (
              <Row gutter={[16, 16]}>
                {featuresData.limitKeyData.map((feature, idx) => {
                  const usage = featuresData.featureUsageData?.usage?.[feature.feature_key] || 0;
                  const limit = feature.limit;
                  const pct = limit ? Math.min((usage / limit) * 100, 100).toFixed(1) : 0;
                  const isUnlimited = !limit || limit === null;

                  return (
                    <Col xs={24} sm={12} md={8} key={idx}>
                      <Card size="small" hoverable>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text strong style={{ textTransform: 'capitalize' }}>
                            {feature.name || feature.feature_key?.replace(/_/g, ' ')}
                          </Text>
                          {feature.enabled ? (
                            <Tag color="green" icon={<CheckCircleOutlined />}>Enabled</Tag>
                          ) : (
                            <Tag color="red" icon={<CloseCircleOutlined />}>Disabled</Tag>
                          )}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary">
                            Usage: <Text strong>{usage}</Text> / {isUnlimited ? '∞' : limit}
                          </Text>
                        </div>
                        {!isUnlimited && (
                          <Progress
                            percent={Number(pct)}
                            size="small"
                            status={pct > 90 ? 'exception' : pct > 70 ? 'active' : 'success'}
                            strokeColor={pct > 90 ? '#ff4d4f' : pct > 70 ? '#faad14' : '#52c41a'}
                          />
                        )}
                        {isUnlimited && (
                          <Progress percent={0} size="small" format={() => 'Unlimited'} />
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <Empty description="No feature data available" />
            )}
          </Card>

          {/* Enabled Modules */}
          {ws.modules_enabled?.length > 0 && (
            <Card title="Enabled Modules" style={{ marginTop: 16 }}>
              <Space wrap>
                {ws.modules_enabled.map((module, idx) => (
                  <Tag key={idx} color="blue" style={{ padding: '4px 12px', fontSize: 13 }}>
                    {module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Tag>
                ))}
              </Space>
            </Card>
          )}
        </Spin>
      ),
    },
  ];

  return (
    <Animate type="pop">
      <div>
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/dashboard"><HomeOutlined /> Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item href="/workspaces">Workspaces</Breadcrumb.Item>
          <Breadcrumb.Item>{ws.name || 'Detail'}</Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/workspaces')}
              >
                Back
              </Button>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {ws.name}
                  <Tag color={ws.active ? 'green' : 'red'} style={{ marginLeft: 12 }}>
                    {ws.active ? 'Active' : 'Inactive'}
                  </Tag>
                </Title>
                <Text type="secondary">{ws.slug} • {ws.plan_name || ws.plan}</Text>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                Refresh
              </Button>
              {can('workspaces', 'view') && (
                <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
              )}
              {can('workspaces', 'edit') && (
                <Button
                  icon={<PoweroffOutlined />}
                  danger={ws.active}
                  onClick={handleToggleStatus}
                  loading={mutationLoading}
                >
                  {ws.active ? 'Deactivate' : 'Activate'}
                </Button>
              )}
            </Space>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
          size="large"
        />
      </div>
    </Animate>
  );
}